import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Card,
  Step,
  StepLabel,
  Stepper,
  Grid,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  TravelExplore as TravelExploreIcon,
  CloudUpload as CloudUploadIcon,
  EmojiEvents as EmojiEventsIcon,
  RocketLaunch as RocketLaunchIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Domain as DomainIcon,
  Lan as LanIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import { notifyCreditsChanged } from '../../services/creditsApi';

const HeaderCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${PRIMARYCOLOR} 0%, ${darken(PRIMARYCOLOR, 0.5)} 100%)`,
  color: '#fff',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
}));

const SectionCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  borderRadius: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
}));

const StepIconContainer = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active
    ? theme.palette.primary.main
    : ownerState.completed
    ? theme.palette.success.main
    : theme.palette.grey[300],
  color: '#fff',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: ownerState.active ? '0 0 0 6px rgba(25,118,210,0.15)' : 'none'
}));

const AssetTypeChip = ({ type }) => {
  const meta = {
    domain: { label: 'Domain', icon: <DomainIcon fontSize="small" /> },
    ip: { label: 'IP', icon: <LanIcon fontSize="small" /> },
    cidr: { label: 'CIDR', icon: <PublicIcon fontSize="small" /> }
  }[type] || { label: type, icon: null };

  return (
    <Chip
      size="small"
      icon={meta.icon}
      label={meta.label}
      sx={{ fontWeight: 600, bgcolor: 'rgba(0,0,0,0.04)' }}
    />
  );
};

function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  const mapping = {
    1: <AssignmentIcon fontSize="small" />,
    2: <TravelExploreIcon fontSize="small" />,
    3: <CloudUploadIcon fontSize="small" />,
    4: <EmojiEventsIcon fontSize="small" />,
    5: <RocketLaunchIcon fontSize="small" />
  };
  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {mapping[icon] || <AssignmentIcon fontSize="small" />}
    </StepIconContainer>
  );
}

const steps = ['Project Details', 'Add Assets', 'Deploy Agent', 'Done'];

function normalizeDomain(value) {
  if (!value) return '';
  let v = value.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, '');     // strip protocol
  v = v.replace(/^www\./, '');           // strip www.
  v = v.split('/')[0];                   // strip paths
  v = v.replace(/:\d+$/, '');            // strip port if provided
  return v;
}

function isValidDomain(v) {
  // Very light domain validation (RFC-complete regex is overkill here)
  const re = /^(?!\-)(?:[a-z0-9\-]{1,63}\.)+[a-z]{2,}$/i;
  return re.test(v);
}

function isValidIPv4(v) {
  const re =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
  return re.test(v);
}

function isValidCIDR(v) {
  const parts = v.split('/');
  if (parts.length !== 2) return false;
  const [ip, mask] = parts;
  if (!isValidIPv4(ip)) return false;
  const m = Number(mask);
  return Number.isInteger(m) && m >= 0 && m <= 32;
}

const CreateExternalPentest = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [organizationId, setOrganizationId] = useState(-1);
  const [organizations, setOrganizations] = useState([{ id: -1, name: 'None' }]);
  const [notes, setNotes] = useState('');
  const [assets, setAssets] = useState([]); // [{id, type, value}]
  const [assetType, setAssetType] = useState('domain');
  const [assetInput, setAssetInput] = useState('');
  const [assetError, setAssetError] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [pentestId, setPentestId] = useState('');
  const [readmeContent, setReadmeContent] = useState('');


  const prependNone = (list = []) => {
  const cleaned = (list || []).filter(o => o && Number(o.id) !== -1);
  return [{ id: -1, name: 'None' }, ...cleaned];
};

function loadOrganizationsBasic() {
  const token = sessionStorage.getItem('token');
  if (!token) {
    try { navigate('/'); } catch { /* optional if navigate not available */ }
    return;
  }
  fetch(`${BASEURL}/organizations?basic=1`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
    .then(r => r.json())
    .then(data => {
      if (data?.error) {
        console.error(data.message || 'Error fetching organizations (basic)');
        setOrganizations([{ id: -1, name: 'None' }]);
        setOrganizationId(-1);
        return;
      }
      const list = Array.isArray(data?.organizations) ? data.organizations : [];
      setOrganizations(prependNone(list));
      setOrganizationId(-1); // default selection
    })
    .catch(err => {
      console.error('Failed to fetch organizations (basic):', err);
      setOrganizations([{ id: -1, name: 'None' }]);
      setOrganizationId(-1);
    });
}

// call once on mount
useEffect(() => {
  loadOrganizationsBasic();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const handleAddAsset = () => {
    let raw = assetInput.trim();

    // Normalize + validate by type
    if (assetType === 'domain') {
      const v = normalizeDomain(raw);
      if (!v) return setAssetError('Enter a domain (e.g., example.com).');
      if (!isValidDomain(v)) return setAssetError('That doesn’t look like a valid domain.');
      if (assets.some((a) => a.type === 'domain' && a.value === v)) return setAssetError('Domain already added.');
      setAssets((prev) => [...prev, { id: crypto.randomUUID(), type: 'domain', value: v }]);
    } else if (assetType === 'ip') {
      if (!isValidIPv4(raw)) return setAssetError('Enter a valid IPv4 address (e.g., 203.0.113.10).');
      if (assets.some((a) => a.type === 'ip' && a.value === raw)) return setAssetError('IP already added.');
      setAssets((prev) => [...prev, { id: crypto.randomUUID(), type: 'ip', value: raw }]);
    } else if (assetType === 'cidr') {
      if (!isValidCIDR(raw)) return setAssetError('Enter a valid CIDR (e.g., 192.168.1.0/24).');
      if (assets.some((a) => a.type === 'cidr' && a.value === raw)) return setAssetError('CIDR already added.');
      setAssets((prev) => [...prev, { id: crypto.randomUUID(), type: 'cidr', value: raw }]);
    }

    setAssetInput('');
    setAssetError('');
  };

  const canAdd = useMemo(() => assetInput.trim().length > 0, [assetInput]);

  const handleDeleteAsset = (id) => {
    setAssets((prev) => prev.filter((a) => a.id !== id));
  };

  const handleClearAssets = () => setAssets([]);

  const handleSavePentest = () => {
    const token = sessionStorage.getItem('token');
    setIsDeploying(true);

    {/* Loop assets and pull value to store in scope*/}
    const scopeAssets = assets.map(a => String(a.value).trim()) .filter(Boolean) .join('\n');
    

    fetch(`${BASEURL}/pentests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        name: projectName,
        scope: scopeAssets, // keep backend unchanged
        pentest_type: 'External Pentest',
        organizationId: organizationId,
      })
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to save pentest project');
        return response.json();
      })
      .then((data) => {
        if (data && data.pentestId) {
          notifyCreditsChanged({ reason: 'pentest-created', pentestId: data.pentestId });
          setPentestId(data.pentestId);
          setIsDeploying(false);
          setActiveStep(3); // Done
        } else {
          console.error('Pentest ID not found in response:', data);
        }
      })
      .catch((error) => {
        console.error('Error saving pentest project:', error);
        setIsDeploying(false);
      });
  };



  const copyValue = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
    } catch {
      /* noop */
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1, width: '100%', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <HeaderCard>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Create External Pentest Project
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Spin up your cloud pentest agent and define assets in minutes. Domains, IPs, and CIDR ranges supported.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              icon={<TravelExploreIcon />}
              label="AI Agents"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
            />
            <Chip
              icon={<RocketLaunchIcon />}
              label="Fast setup"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={idx}>
                  <StepLabel StepIconComponent={CustomStepIcon} sx={{ color: '#fff' }}>
                    <Typography sx={{ color: '#fff' }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </HeaderCard>

      {/* Step 0: Project Details */}
      {activeStep === 0 && (
        <SectionCard sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Project Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Give your pentest a clear name so your team can find it later.
              </Typography>
              <TextField
                fullWidth
                label="Project Name"
                placeholder="e.g., Acme External Pentest – Q3"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth>
                <InputLabel id="org-select-label">Organization</InputLabel>
                <Select
                  labelId="org-select-label"
                  label="Organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(Number(e.target.value))}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                  disabled={!projectName.trim()}
                >
                  Next
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  width: '100%',
                  minHeight: 220,
                  borderRadius: 3,
                  p: 3,
                  bgcolor: 'rgba(25,118,210,0.06)',
                  border: '1px dashed rgba(25,118,210,0.25)'
                }}
              >
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Tips
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use a descriptive name that includes the company or project name and the pentest type (e.g., External, Web, etc.) to make it easy to identify later.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* Step 1: Add Assets */}
      {activeStep === 1 && (
        <SectionCard sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Add Assets (Domain / IP / CIDR)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Define the scope precisely. We’ll normalize domains and validate IPs/CIDRs. You can add multiple.
          </Typography>

          <Grid container spacing={2} alignItems="center">
  <Grid item xs={12} md="auto">
    <ToggleButtonGroup
      value={assetType}
      exclusive
      onChange={(_, v) => v && setAssetType(v)}
      size="small"
      color="primary"
    >
      <ToggleButton value="domain" sx={{ height: 44 }}><DomainIcon sx={{ mr: 1 }} />Domain</ToggleButton>
      <ToggleButton value="ip" sx={{ height: 44 }}><LanIcon sx={{ mr: 1 }} />IP</ToggleButton>
      <ToggleButton value="cidr" sx={{ height: 44 }}><PublicIcon sx={{ mr: 1 }} />CIDR</ToggleButton>
    </ToggleButtonGroup>
  </Grid>

  {/* Center the TextField and make its visual height match */}
  <Grid item xs={12} md sx={{ display: 'flex', alignItems: 'center' }}>
    <TextField
      fullWidth
      size="small"
      label={
        assetType === 'domain'
          ? 'Enter domain (e.g., https://example.com)'
          : assetType === 'ip'
          ? 'Enter IPv4 (e.g., 203.0.113.10)'
          : 'Enter CIDR (e.g., 192.168.1.0/24)'
      }
      value={assetInput}
      onChange={(e) => {
        setAssetInput(e.target.value);
        setAssetError('');
      }}
      error={!!assetError}
      helperText={assetError ? assetError : null} // <-- no blank line when no error
      FormHelperTextProps={{ sx: { m: assetError ? '3px 14px 0' : 0 } }}
      InputLabelProps={{ shrink: true }}          // keeps label up; prevents extra vertical jiggle
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {assetType === 'domain' ? <DomainIcon /> : assetType === 'ip' ? <LanIcon /> : <PublicIcon />}
          </InputAdornment>
        )
      }}
      sx={{
        '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' }, // match height
        '& .MuiInputBase-input': { py: 0 }                                // remove extra inner padding
      }}
    />
  </Grid>

  <Grid item xs={12} md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
    <Button
      startIcon={<AddCircleOutlineIcon />}
      variant="contained"
      onClick={handleAddAsset}
      disabled={!canAdd}
      sx={{ height: 44 }} // match height
    >
      Add
    </Button>
  </Grid>
</Grid>


          {/* Nice table */}
          <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Typography fontWeight={800}>Assets ({assets.length})</Typography>
              <Box>
                <Tooltip title="Remove all assets">
                  <span>
                    <Button color="inherit" size="small" onClick={handleClearAssets} disabled={assets.length === 0}>
                      Clear All
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={140}>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell width={120} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No assets added yet. Add a domain, IP, or CIDR above.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell>
                        <AssetTypeChip type={a.type} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {a.value}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={() => copyValue(a.value)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteAsset(a.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>


          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(2)}
              disabled={assets.length === 0}
            >
              Next
            </Button>
          </Box>
        </SectionCard>
      )}

      {/* Step 2: Install Agent */}
      {activeStep === 2 && (
        <SectionCard sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Agent Deployment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We’ll deploy your external pentesting agent to the cloud.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 200 }}>
            {isDeploying ? (
              <>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Deploying...</Typography>
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ maxWidth: 700, textAlign: 'left', mb: 3 }}>
                  <AlertTitle>Heads up</AlertTitle>
                  Your agent is deployed automatically after you click <b>Next</b>. This usually takes a couple minutes.
                </Alert>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => setActiveStep(1)}>
                    Back
                  </Button>
                  <Button variant="contained" onClick={handleSavePentest}>
                    Next
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {/* README Pane */}
          {readmeContent && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography fontWeight={800} sx={{ mb: 1 }}>
                Quick Reference
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                <ReactMarkdown>{readmeContent}</ReactMarkdown>
              </Box>
            </>
          )}
        </SectionCard>
      )}

      {/* Step 3: Congratulations */}
      {activeStep === 3 && (
        <SectionCard sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)' }}>
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
            You’re all set! 🎉
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            Your pentest project has been created. Let’s kick off the scan or do it later from the dashboard.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={() => { setActiveStep(4); navigate('/pentests'); }}>
              Finish
            </Button>
          </Box>
        </SectionCard>
      )}

    </Box>
  );
};

export default CreateExternalPentest;
